import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, setDoc, onSnapshot, serverTimestamp, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { adminEmails } from '../data/admins';

// --- MOCK DATA DEFINITIONS ---

const mockProductLibrary = {
    cleanser: { name: 'Gentle Cleanser', category: 'Cleanser', image: 'https://placehold.co/100x100/f1f5f9/334155?text=Cleanser' },
    toner: { name: 'Hydrating Toner', category: 'Toner', image: 'https://placehold.co/100x100/f1f5f9/334155?text=Toner' },
    vitaminC: { name: 'Vitamin C Serum', category: 'Serum', image: 'https://placehold.co/100x100/f1f5f9/334155?text=Serum' },
    moisturizer: { name: 'Daily Moisturizer', category: 'Moisturizer', image: 'https://placehold.co/100x100/f1f5f9/334155?text=Moisturizer' },
    sunscreen: { name: 'SPF 50 Sunscreen', category: 'Sunscreen', image: 'https://placehold.co/100x100/f1f5f9/334155?text=Sunscreen' },
    exfoliant: { name: 'AHA/BHA Exfoliant', category: 'Exfoliant', image: 'https://placehold.co/100x100/f1f5f9/334155?text=Exfoliant' },
    retinol: { name: 'Retinol Serum', category: 'Serum', image: 'https://placehold.co/100x100/f1f5f9/334155?text=Retinol' },
    nightCream: { name: 'Rich Night Cream', category: 'Moisturizer', image: 'https://placehold.co/100x100/f1f5f9/334155?text=Night+Cream' },
    mask: { name: 'Exfoliating Mask', category: 'Mask', image: 'https://placehold.co/100x100/f1f5f9/334155?text=Mask' },
};

const mockRoutine = {
    'Monday': {
        AM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.toner, step: 2 }, { ...mockProductLibrary.vitaminC, step: 3 }, { ...mockProductLibrary.moisturizer, step: 4 }, { ...mockProductLibrary.sunscreen, step: 5 } ],
        PM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.exfoliant, step: 2 }, { ...mockProductLibrary.retinol, step: 3 }, { ...mockProductLibrary.nightCream, step: 4 } ]
    },
    'Tuesday': {
        AM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.toner, step: 2 }, { ...mockProductLibrary.vitaminC, step: 3 }, { ...mockProductLibrary.moisturizer, step: 4 }, { ...mockProductLibrary.sunscreen, step: 5 } ],
        PM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.retinol, step: 2 }, { ...mockProductLibrary.nightCream, step: 3 } ]
    },
    'Wednesday': {
        AM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.toner, step: 2 }, { ...mockProductLibrary.vitaminC, step: 3 }, { ...mockProductLibrary.moisturizer, step: 4 }, { ...mockProductLibrary.sunscreen, step: 5 } ],
        PM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.exfoliant, step: 2 }, { ...mockProductLibrary.retinol, step: 3 }, { ...mockProductLibrary.nightCream, step: 4 } ]
    },
    'Thursday': {
        AM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.toner, step: 2 }, { ...mockProductLibrary.vitaminC, step: 3 }, { ...mockProductLibrary.moisturizer, step: 4 }, { ...mockProductLibrary.sunscreen, step: 5 } ],
        PM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.retinol, step: 2 }, { ...mockProductLibrary.nightCream, step: 3 } ]
    },
    'Friday': {
        AM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.toner, step: 2 }, { ...mockProductLibrary.vitaminC, step: 3 }, { ...mockProductLibrary.moisturizer, step: 4 }, { ...mockProductLibrary.sunscreen, step: 5 } ],
        PM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.exfoliant, step: 2 }, { ...mockProductLibrary.retinol, step: 3 }, { ...mockProductLibrary.nightCream, step: 4 } ]
    },
    'Saturday': {
        AM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.toner, step: 2 }, { ...mockProductLibrary.moisturizer, step: 3 }, { ...mockProductLibrary.sunscreen, step: 4 } ],
        PM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.nightCream, step: 2 } ],
        Weekly: [ { ...mockProductLibrary.mask, step: 1 } ]
    },
    'Sunday': {
        AM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.toner, step: 2 }, { ...mockProductLibrary.moisturizer, step: 3 }, { ...mockProductLibrary.sunscreen, step: 4 } ],
        PM: [ { ...mockProductLibrary.cleanser, step: 1 }, { ...mockProductLibrary.nightCream, step: 2 } ]
    },
};

const generateProgress = (routine, days) => {
    const progress = {};
    const today = new Date();
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    for (let i = 0; i < days; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayName = daysOfWeek[date.getDay()];

        const dayRoutine = routine[dayName];
        if (!dayRoutine) continue;

        progress[dateStr] = {};
        ['AM', 'PM', 'Weekly'].forEach(time => {
            if (dayRoutine[time]) {
                progress[dateStr][time] = {};
                dayRoutine[time].forEach(product => {
                    const isCore = ['cleanser', 'moisturizer', 'sunscreen'].some(p => product.name.toLowerCase().includes(p));
                    const completionChance = isCore ? 0.95 : 0.8;
                    progress[dateStr][time][product.name] = Math.random() < completionChance ? 'completed' : 'skipped';
                });
            }
        });
    }
    return progress;
};

export const UserDataContext = createContext({
  user: null,
  userData: null,
  isAdmin: false,
  loading: true,
  updateUserData: async () => ({ success: false, error: 'Not initialized' }),
});

export const useUserDataContext = () => useContext(UserDataContext);

export const UserDataProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const authUnsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        const userRef = doc(db, 'users', authUser.uid);

        // Special seeding logic for the demo user
        if (authUser.email === 'rize@inaragroups.com') {
            const docSnap = await getDoc(userRef);
            if (!docSnap.exists()) {
                // If the demo user doesn't exist, create their profile with mock data ONCE.
                console.log('Seeding new demo user profile...');
                const initialMockData = {
                    email: authUser.email,
                    uid: authUser.uid,
                    displayName: authUser.displayName || 'Rize Demo',
                    photoURL: authUser.photoURL || `https://ui-avatars.com/api/?name=Rize+Demo&background=f1f5f9&color=334155`,
                    createdAt: serverTimestamp(),
                    isAdmin: true,
                    points: 1500,
                    checkInStreak: 7,
                    totalCheckIns: 25,
                    lastCheckIn: new Date(Date.now() - 24 * 60 * 60 * 1000),
                    goals: [
                        { id: Date.now(), title: 'Reduce acne breakouts by 50%', progress: 59, target: 100, deadline: '2024-12-31' },
                    ],
                    routine: mockRoutine,
                    progress: generateProgress(mockRoutine, 90),
                    assessmentCompleted: true,
                };
                await setDoc(userRef, initialMockData);
            } else {
                // If the user exists, check if their routine is empty. If so, seed it.
                const existingData = docSnap.data();
                if (!existingData.routine || Object.keys(existingData.routine).length === 0) {
                    console.log('Demo user has an empty routine. Seeding initial routine.');
                    await updateDoc(userRef, {
                        routine: mockRoutine,
                        progress: generateProgress(mockRoutine, 90),
                        assessmentCompleted: true,
                    });
                }
                // If routine exists, we DO NOTHING, preserving the user's saved data.
            }
        }

        // Real-time listener for ALL users (including the demo user)
        const dataUnsubscribe = onSnapshot(userRef, (docSnap) => {
          if (docSnap.exists()) {
            setUserData(docSnap.data());
            setIsAdmin(adminEmails.includes(docSnap.data().email) || docSnap.data().isAdmin === true);
          } else if (authUser.email !== 'rize@inaragroups.com') {
            // Create a new, empty document for any non-demo, non-existent user
            const newUser = {
              email: authUser.email,
              uid: authUser.uid,
              displayName: authUser.displayName,
              photoURL: authUser.photoURL,
              createdAt: serverTimestamp(),
              points: 0,
              checkInStreak: 0,
              totalCheckIns: 0,
              lastCheckIn: null,
              routine: {},
              progress: {},
              goals: [],
              assessmentCompleted: false,
            };
            setDoc(userRef, newUser).then(() => setUserData(newUser));
          }
          setUser(authUser);
          setLoading(false);
        }, (error) => {
          console.error("Error with snapshot listener:", error);
          setLoading(false);
        });

        return () => dataUnsubscribe();
      } else {
        setUser(null);
        setUserData(null);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => authUnsubscribe();
  }, []);

  const updateUserData = async (newData) => {
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      try {
        await updateDoc(userRef, { ...newData, lastUpdated: serverTimestamp() });
        return { success: true };
      } catch (error) {
        console.error('Error updating user data:', error);
        return { success: false, error };
      }
    }
    return { success: false, error: 'No user is authenticated' };
  };

  const value = { user, userData, isAdmin, loading, updateUserData };

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};
